/**
 * FormattedAIResponse - Backward compatible wrapper for BlockRenderer
 * Uses the new block-based rendering pipeline (no regex preprocessing)
 */

import * as React from 'react';
import { BlockRenderer } from './BlockRenderer';
import 'katex/dist/katex.min.css';
import 'katex/contrib/mhchem/mhchem.js';

import { CitationSource } from '@/utils/citationReplacer';

interface FormattedAIResponseProps {
  content: string;
  isStreaming?: boolean;
  onComplete?: () => void;
  isDarkMode?: boolean;
  className?: string;
  modelName?: string;
  agentName?: string;
  citationSources?: CitationSource[];
}

export const FormattedAIResponse: React.FC<FormattedAIResponseProps> = ({
  content,
  isStreaming = false,
  onComplete,
  isDarkMode = false,
  className = '',
  modelName,
  agentName,
  citationSources,
}) => {
  // Minimal normalization for <pre><code> and <br> before parsing
  const normalized = React.useMemo(() => {
    if (!content) return '';
    let processed = content;

    // Some providers accidentally return a JSON string literal (quoted + escaped) instead of raw text.
    // Example: "line1\\n\\n```js...". Decode it so markdown parsing works.
    try {
      const trimmed = processed.trim();
      if (
        trimmed.length >= 2 &&
        trimmed.startsWith('"') &&
        trimmed.endsWith('"') &&
        (trimmed.includes('\\n') || trimmed.includes('\\"') || trimmed.includes('```'))
      ) {
        const decoded = JSON.parse(trimmed);
        if (typeof decoded === 'string') processed = decoded;
      }
    } catch {
      // ignore
    }

    processed = processed
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&amp;/gi, '&')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      .replace(/&#(\d+);/g, (_m, dec) => String.fromCharCode(parseInt(dec, 10)))
      .replace(/&#x([0-9a-f]+);/gi, (_m, hex) => String.fromCharCode(parseInt(hex, 16)));

    // Strip odd academic/search-style citation artifacts such as:
    //    or 【2】 or other fullwidth-bracket segments.
    // These often come from upstream search tooling and are not useful in chat.
    processed = processed.replace(/【[^】]*】/g, '');

    // Also remove common bracketed line-reference patterns like [1†L12-L15]
    processed = processed.replace(/\[\d+†L\d+[-–]\d+]/g, '');

    // Convert literal escaped sequences (\\n, \\t, \\r) into real whitespace
    // Detect escaped newlines in various contexts including before markdown formatting
    if (/\\n\s*[A-Z\-_*\[\(#]/i.test(processed) || processed.includes('\\n\\n') || /\)\s*\\n/.test(processed) || /\\n\*\*/.test(processed)) {
      // Avoid touching code fences by splitting and rejoining
      const parts = processed.split(/(```[\s\S]*?```)/g);
      processed = parts
        .map((seg) => seg.startsWith('```')
          ? seg
          : seg
            .replace(/\\r\\n/g, '\n')
            .replace(/\\n/g, '\n')
            .replace(/\\t/g, '  ')
        )
        .join('');
    }

    processed = processed.replace(
      /<pre>\s*<code(?:\s+class="language-([^"]+)")?>([\s\S]*?)<\/code>\s*<\/pre>/gi,
      (_match, lang = '', codeContent = '') => {
        const language = typeof lang === 'string' ? lang.trim() : '';
        const decodedCode = codeContent
          .replace(/&lt;/gi, '<')
          .replace(/&gt;/gi, '>')
          .replace(/&amp;/gi, '&')
          .replace(/&quot;/gi, '"')
          .replace(/&#39;/gi, "'")
          .replace(/^\n+/, '')
        const fenceStart = language ? `\n\n\`\`\`${language}\n` : '\n\n```\n';
        return `${fenceStart}${decodedCode}\n\`\`\`\n\n`;
      }
    );

    processed = processed.replace(/<br\s*\/?>(?=\n|$)/gi, '  \n');

    // During streaming, prevent stray asterisks/underscores from showing when emphasis is incomplete
    if (isStreaming) {
      // Process line by line to avoid interfering with fenced code blocks mid-stream
      const sanitizeLine = (line: string): string => {
        // Skip lines that look like code fences
        if (/^\s*```/.test(line)) return line;
        // Remove unmatched leading emphasis markers (one or two asterisks/underscores)
        line = line.replace(/(^|\s)(\*\*|__)([^*_\n]+)?$/g, (_m, p1, _em, rest) => `${p1}${rest ?? ''}`);
        line = line.replace(/(^|\s)(\*|_)([^*_\n]+)?$/g, (_m, p1, _em, rest) => `${p1}${rest ?? ''}`);
        // Remove unmatched trailing emphasis markers
        line = line.replace(/([^*_\n]+?)(\*\*|__)$/.source ? new RegExp('([^*_\n]+?)(\\*\\*|__)$') : /$/, (_m: string, text: string) => text || '');
        line = line.replace(/([^*_\n]+?)(\*|_)$/.source ? new RegExp('([^*_\n]+?)(\\*|_)$') : /$/, (_m: string, text: string) => text || '');
        return line;
      };
      processed = processed
        .split('\n')
        .map(sanitizeLine)
        .join('\n');
    }

    return processed;
  }, [content, isStreaming]);

  return (
    <BlockRenderer
      content={normalized}
      isStreaming={isStreaming}
      onComplete={onComplete}
      isDarkMode={isDarkMode}
      className={className}
      modelName={modelName}
      agentName={agentName}
      citationSources={citationSources}
    />
  );
};
